/**
 * "Where was the user going?" — ONE implementation.
 *
 * TWO DIFFERENT RETURN-TO PROBLEMS EXIST IN THIS APP, and collapsing them would
 * break both:
 *
 *  1. AUTH return-to spans a FULL PAGE ROUND TRIP through Entra. That is MSAL's
 *     own `redirectStartPage` (see redirectForApiScope), it is captured from
 *     window.location.href at redirect time, and it cannot be replaced by
 *     anything here. Do not reimplement it.
 *
 *  2. IN-APP return-to spans a client-side detour: the user asked for a route,
 *     a gate sent them somewhere else first (project selection), and afterwards
 *     they should continue to what they originally asked for.
 *
 * THIS MODULE IS (2), and it is the single implementation the projectSelected
 * guard calls into. A second copy that has to agree with this one is exactly the
 * failure mode we have already paid for twice in this codebase.
 *
 * NOT A URL PARAMETER, deliberately. `?returnTo=` leaks into logs, pasted links
 * and referrers, and an attacker-supplied value is an open redirect unless every
 * consumer validates it. sessionStorage keeps it out of the URL entirely, and
 * the validation below is then the only gate rather than one of several.
 */

const INTENDED_URL_KEY = 'vericureIntendedUrl';

/**
 * Only same-origin, absolute-path URLs are storable.
 *
 * Rejects "//evil.com" (protocol-relative, which a naive startsWith('/') check
 * lets straight through), any scheme, and anything not rooted at '/'. This is
 * the open-redirect gate; keep it here so there is one of them.
 */
export function isSafeInternalUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false;
  // A backslash is treated as a slash by some parsers; refuse rather than guess.
  if (url.includes('\\')) return false;
  return true;
}

/**
 * Remember where the user was heading, before a gate redirects them.
 *
 * Silently ignores an unsafe or empty URL: a gate should still be able to
 * redirect even if there is nothing worth coming back to.
 */
export function rememberIntendedUrl(url: string | null | undefined): void {
  if (!isSafeInternalUrl(url)) return;
  try {
    sessionStorage.setItem(INTENDED_URL_KEY, url as string);
  } catch {
    // Private mode / storage blocked. The detour still works; the user just
    // lands on the default afterwards instead of their original target.
  }
}

/** Look without consuming — for a gate deciding whether a detour is pending. */
export function peekIntendedUrl(): string | null {
  try {
    const url = sessionStorage.getItem(INTENDED_URL_KEY);
    return isSafeInternalUrl(url) ? url : null;
  } catch {
    return null;
  }
}

/**
 * Take it and clear it — a stored destination is single-use.
 *
 * Leaving it behind means the NEXT unrelated navigation gets hijacked back to a
 * stale destination, which reads as the app randomly jumping somewhere the user
 * did not ask for.
 */
export function consumeIntendedUrl(): string | null {
  const url = peekIntendedUrl();
  try {
    sessionStorage.removeItem(INTENDED_URL_KEY);
  } catch {
    // ignore
  }
  return url;
}

/** Drop any pending destination — e.g. on sign-out or a deliberate cancel. */
export function forgetIntendedUrl(): void {
  try {
    sessionStorage.removeItem(INTENDED_URL_KEY);
  } catch {
    // ignore
  }
}
